import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import site from '../../data/site';
import navLinks from '../../data/nav';
import { categories } from '../../data/collections';
import { legalLinks } from '../../data/legalRoutes';
import Logo from './Logo';
import Icon from '../ui/Icon';
import DotTrail from '../ui/DotTrail';
import Stagger from '../ui/Stagger';

export function Footer() {
  const year = new Date().getFullYear();
  const { pathname } = useLocation();

  // Away from home, section anchors have to route home first.
  const sectionHref = (id) => (pathname === '/' ? `#${id}` : `/#${id}`);

  return (
    <footer className="relative overflow-hidden bg-ink-950 text-canvas">
      {/* A soft sunrise bloom in the corner, echoing the logo mark. */}
      <div
        className="pointer-events-none absolute -left-40 -top-40 h-[26rem] w-[26rem] rounded-full opacity-[0.16] blur-3xl"
        style={{ background: 'radial-gradient(circle, #f9af00 0%, transparent 68%)' }}
        aria-hidden="true"
      />

      <div className="shell relative py-16 lg:py-20">
        <Stagger
          step={90}
          variant="up"
          distance={22}
          className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]"
        >
          <div>
            <Logo tone="light" markSize={48} />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-canvas/55">
              {site.descriptor}. {site.credential}. Our showroom is on C G Road, Ahmedabad.
            </p>
            <DotTrail count={6} tone="gold" className="mt-6" />
          </div>

          <nav aria-label="Footer">
            <h3 className="eyebrow mb-5 text-champagne-300">Explore</h3>
            <ul className="space-y-3 text-sm">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={sectionHref(link.id)}
                    className="text-canvas/60 transition hover:text-champagne-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="eyebrow mb-5 text-champagne-300">Collections</h3>
            <ul className="space-y-3 text-sm">
              {categories
                .filter((category) => category.id !== 'all')
                .map((category) => (
                  <li key={category.id}>
                    <a
                      href={sectionHref('collections')}
                      className="text-canvas/60 transition hover:text-champagne-300"
                    >
                      {category.label}
                    </a>
                  </li>
                ))}
            </ul>
          </div>

          <div>
            <h3 className="eyebrow mb-5 text-champagne-300">Visit &amp; Contact</h3>
            <address className="space-y-4 text-sm not-italic text-canvas/60">
              <p className="flex gap-3">
                <Icon name="pin" size={16} className="mt-0.5 shrink-0 text-champagne-300" />
                <span>
                  {site.address.line1}
                  <br />
                  {site.address.line2}
                  <br />
                  {site.address.city}, {site.address.state} {site.address.postalCode}
                </span>
              </p>
              <p>
                <a
                  href={site.contact.phoneHref}
                  className="flex gap-3 transition hover:text-champagne-300"
                >
                  <Icon name="phone" size={16} className="mt-0.5 shrink-0 text-champagne-300" />
                  <span className="tabular-nums">{site.contact.phoneDisplay}</span>
                </a>
              </p>
              <p>
                <a
                  href={`mailto:${site.contact.email}`}
                  className="flex gap-3 break-all transition hover:text-champagne-300"
                >
                  <Icon name="mail" size={16} className="mt-0.5 shrink-0 text-champagne-300" />
                  <span>{site.contact.email}</span>
                </a>
              </p>
              <p>
                <a
                  href={site.social.instagram}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex gap-3 transition hover:text-champagne-300"
                >
                  <Icon name="instagram" size={16} className="mt-0.5 shrink-0 text-champagne-300" />
                  <span>{site.social.instagramHandle}</span>
                </a>
              </p>
            </address>
          </div>
        </Stagger>

        <div className="mt-14 border-t border-canvas/10 pt-7">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-3 text-[0.72rem] uppercase tracking-widest text-canvas/40">
              <p>
                © {year} {site.legalName}
              </p>
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {legalLinks.map((doc) => (
                  <li key={doc.slug}>
                    <Link to={doc.path} className="transition hover:text-champagne-300">
                      {doc.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="max-w-md sm:text-right">
              <p className="text-[0.72rem] leading-relaxed text-canvas/35">
                Prices and availability confirmed in showroom. Images are of pieces previously
                stocked and are shown as a guide to our range.
              </p>
              <p className="mt-3 text-[0.7rem] uppercase tracking-widest text-canvas/40">
                {site.credit.label}{' '}
                <a
                  href={site.credit.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-canvas/60 underline decoration-canvas/20 underline-offset-4 transition hover:text-champagne-300 hover:decoration-champagne-300"
                >
                  {site.credit.name}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
